import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { MetricToggleChart } from "@/components/metric-chart";
import { DataTable } from "@/components/data-table";
import { DOW_LABELS, HOUR_LABELS } from "@/components/analytics-ui";
import { ChartCard, EmptyChart } from "@/components/charts/chart-card";
import { RankList } from "@/components/charts/rank-list";
import { AttributionCard } from "@/components/charts/attribution-card";
import { BarChart } from "@/components/charts/bar-chart";
import { Heatmap } from "@/components/charts/heatmap";
import { ProgressRing } from "@/components/charts/progress-ring";
import { ParamChips } from "@/components/analytics/param-chips";
import { CompareNote, DataNote } from "@/components/analytics/report-shell";
import { StoreSampleBanner } from "@/components/analytics/store-sample-banner";
import { SkStorePage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import { defaultGrain, grainOptions, withRange } from "@shared/store-report";
import { fmtNum } from "@shared/format";
import type { SalesPage } from "@shared/pages/sales";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/sales")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<SalesPage>("sales", deps));
  },
  component: SalesRoute,
});

function SalesRoute() {
  useTitle("Sales");
  const search = Route.useSearch();
  return (
    <PageQuery<SalesPage> name="sales" search={search} skeleton={<SkStorePage row="wide" />}>
      {(page) => <Sales page={page} />}
    </PageQuery>
  );
}

function Sales({ page }: { page: SalesPage }) {
  const { range: r, status } = page;
  const cur = page.newVsReturning;
  const peak = page.whenPeopleBuy.peak;
  const months = page.months;

  return (
    <div className="page-shell">
      <PageHeader title="Sales" actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} compare />} />
      <StoreSampleBanner sales={page.sources.sales === "sample"} />
      <DataNote status={status} staleAfterDays={3} />
      {status && !status.onFile.orders && (
        <SetupPanel title="No Shopify orders on file yet" className="mb-4">
          Shopify is connected. Orders show here after the first sync finishes.
        </SetupPanel>
      )}

      <MetricToggleChart
        labels={page.labels}
        metrics={page.metrics}
        height="clamp(200px, 17cqw, 280px)"
        hint={`From Shopify.${page.hasPrevious ? " Dashed lines are the previous period." : ""}`}
        right={<ParamChips param="by" options={grainOptions()} defaultValue={defaultGrain(r)} />}
      />
      <CompareNote range={r} />

      <div className="mt-4 grid gap-4 @5xl:grid-cols-3">
        <ChartCard className="@5xl:col-span-2" title="Last 13 months" hint="Each bar is a calendar month. A dash means no orders are on file for that month yet.">
          <BarChart
            labels={months.labels}
            series={[
              { name: "First-time customers", color: "var(--primary)", values: months.newRevenue },
              { name: "Returning customers", color: "var(--accent)", values: months.returningRevenue },
              ...(months.unknownRevenue.some((v) => v > 0) ? [{ name: "Not known yet", color: "var(--muted)", values: months.unknownRevenue }] : []),
            ]}
            hollow={months.hollow}
            labelEvery={1}
            emptyText="No orders on file yet."
          />
        </ChartCard>
        <AttributionCard title="Where orders came from" views={page.attribution} />
      </div>

      <div className="mt-4 grid gap-4 @5xl:grid-cols-3">
        <ChartCard className="@5xl:col-span-2" title="When people buy" hint="Eastern time. Darker = more sales.">
          {peak ? (
            <Heatmap rows={DOW_LABELS} cols={HOUR_LABELS} values={page.whenPeopleBuy.totals} details={page.whenPeopleBuy.details} peak={peak} />
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
        <ChartCard title="New vs returning" hint="First-time: the customer's first order with the store.">
          {cur.orders > 0 ? (
            <ProgressRing pct={cur.firstShare} label="first orders">
              <p>
                {fmtNum(cur.newCustomerOrders)} of {plural(cur.orders, "order")} {cur.orders === 1 ? "was" : "were"} a customer&apos;s first.
              </p>
              <p className="mt-1">{fmtNum(cur.returningCustomerOrders)} came from returning customers.</p>
              {cur.unknownCustomerOrders > 0 && <p className="mt-1">{fmtNum(cur.unknownCustomerOrders)} not known yet.</p>}
            </ProgressRing>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
      </div>

      <div className="mt-4 grid gap-4 @5xl:grid-cols-3">
        <ChartCard
          title="Top products"
          right={
            <Link href={withRange("/products", r.params)} className="text-xs font-semibold text-accent-dark hover:underline">
              All products
            </Link>
          }
          hint="Shopify sales by product, from Shopify."
        >
          <RankList items={page.products.items} emptyText="No products sold in this range." />
        </ChartCard>
        <ChartCard className="@5xl:col-span-2" title="Recent orders" hint="The latest orders on file, whatever the range. Eastern time.">
          <DataTable columns={page.orders.columns} rows={page.orders.rows} rowKey="id" defaultSort="when" pageSize={15} emptyText="No orders on file yet." />
        </ChartCard>
      </div>
    </div>
  );
}

function plural(n: number, one: string): string {
  return `${fmtNum(n)} ${Math.round(n) === 1 ? one : `${one}s`}`;
}
